import React from 'react';
// @ts-ignore
import * as d3 from 'd3';
import styled from 'styled-components';
// @ts-ignore
import classNames from 'classnames';

import BorderBox from './BorderBox';
import HorizontalAxis from './HorizontalAxis';
import VerticalAxis from './VerticalAxis';
import { groupForStacked } from '../../../services/helpers';

export const MARGIN = {
  top: 20,
  bottom: 40,
  left: 100,
  right: 20
};

const SERIES_COLORS: any = {
  'G7': {
    'stroke': '#1E6BD1',
    'fill': '#779DCF'
  },
  'BRICS': {
    'stroke': '#4E8F7E',
    'fill': '#8EC1B6'
  },
  'REM': {
    'stroke': '#DA7F52',
    'fill': '#D5987D'
  },
  'RDM': {
    'stroke': '#A44FD1',
    'fill': '#B99CC4'
  }
};

interface BpsRecord {
  region: string,
  year: number,
  bps: number,
}

interface AreaPoint {
  x: number,
  lowerY: number,
  upperY: number,
}

interface BpsSeriesProps {
  points: any[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleLinear<number, number>,
  stroke: string,
  fill: string,
  chartHeight: number,
  isDisabled: boolean,
};

const StyledBpsSeries = styled.path`
  stroke-width: 5px;
  stroke-linecap: round;
  stroke-linejoin: miter;
  box-shadow: 0px 6px 2px #00000029;

  &.is-disabled {
    stroke-opacity: 0.25;
    fill-opacity: 0.25;
  }
`;

const BpsSeries = ({ points, xScale, yScale, stroke, fill, chartHeight, isDisabled }: BpsSeriesProps) => {
  const dictPoints = points.map(x => ([x.data.year, x[0], x[1]]));
  const line = d3
    .area()
    // @ts-ignore
    .x((d) => xScale(d[0]))
    // @ts-ignore
    .y0((d) => yScale(d[1]))
    // @ts-ignore
    .y1((d) => yScale(d[2]));

  const sortedPoints = dictPoints.sort((a, b) => a[0] - b[0]);
  // @ts-ignore
  const path = line(sortedPoints);

  return (
    <StyledBpsSeries
      style={{ stroke: stroke, fill: fill }}
      d={path || ''}
      className={ classNames({'is-disabled': isDisabled}) }
    />
  )
};

interface LineChartProps {
  bpsRecords: BpsRecord[],
  chartWidth: number,
  chartHeight: number,
  selectedSeries: string | undefined,
  yDomain?: any
};

const StackedAreaChart = ({ bpsRecords, chartWidth, chartHeight, selectedSeries, yDomain }: LineChartProps) => {
  const xScale = d3.scaleLinear()
    // @ts-ignore
    .domain(d3.extent(bpsRecords.map(record => record.year)))
    .range([MARGIN.left, MARGIN.left + chartWidth]);

  const groups = groupForStacked(bpsRecords);
  const _yDomain = yDomain
    ? yDomain
    // @ts-ignore
    : d3.scaleLinear().domain([0, d3.max(groups[groups.length - 1].map(x => x[1]))]);

  const yScale = _yDomain
    .range([MARGIN.top + chartHeight, MARGIN.top]);

  return (
    <svg
      viewBox={`0 0 ${MARGIN.left + chartWidth + MARGIN.right} ${MARGIN.top + chartHeight + MARGIN.bottom}`}
    >
      { groups.reverse().map((group) => {
          const region = group['key'];

          return (
            <BpsSeries
              key={ region }
              xScale={ xScale }
              yScale={ yScale }
              points={ group }
              chartHeight={ chartHeight }
              stroke={ SERIES_COLORS[region]['stroke'] }
              fill={ SERIES_COLORS[region]['fill'] }
              isDisabled={ selectedSeries !== undefined && selectedSeries !== region }
            />
          );
        })
      }
      <BorderBox chartWidth={ chartWidth } chartHeight={ chartHeight } />
      <VerticalAxis yScale={ yScale }/>
      <HorizontalAxis xScale={ xScale } chartHeight={ chartHeight } />
    </svg>
  )
};

export default StackedAreaChart;
